import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Container,
  CssBaseline,
  Paper,
  Stack,
  Step,
  StepLabel,
  Stepper,
  Typography,
  styled,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import ChatIcon from "@mui/icons-material/Chat";
import { useNavigate, useParams } from "react-router-dom";
import DetailFieldGrid from "./DetailFieldGrid";
import { fetchWhatsappSchemaSolicitudByUuid } from "./whatsappSchemaSolicitudesApi";
import { withBogotaStart } from "./solicitudFechaHora";
import {
  CLIENT_NESTED_FIELDS,
  CLIENT_ROOT_FIELDS,
  COMMERCE_FIELDS,
  CREDIT_FIELDS,
  VIABILITY_FIELDS,
  buildClientFieldValues,
  buildSectionFieldValues,
  formatConversationStatus,
  formatDate,
  formatFieldValue,
  getActiveStepIndex,
  isSectionEmpty,
  sortSteps,
} from "./whatsappSchemaSolicitudFormatters";

const Item = styled(Paper)(({ theme }) => ({
  backgroundColor: theme.palette.mode === "dark" ? "#1A2027" : "#fff",
  ...theme.typography.body2,
  padding: theme.spacing(1),
  textAlign: "center",
  color: theme.palette.text.secondary,
  visibility: "hidden",
}));

const ButtonSecondary = styled(Button)({
  borderRadius: "20px",
  color: "#161e55",
  borderColor: "#161e55",
  textTransform: "none",
});

const ButtonAdd = styled(Button)({
  borderRadius: "20px",
  backgroundColor: "#161e55",
  color: "#fff",
  textTransform: "none",
  "&:hover": {
    backgroundColor: "#A8C8AF",
  },
  "&.Mui-disabled": {
    backgroundColor: "rgba(22, 30, 85, 0.45)",
    color: "#fff",
  },
});

const SectionCard = styled(Card)({
  borderRadius: "18px",
  overflow: "visible",
  boxShadow: "0 4px 16px rgba(22, 30, 85, 0.14)",
  border: "1px solid rgba(22, 30, 85, 0.08)",
});

const SectionTitle = styled(Typography)({
  color: "#161e55",
  fontWeight: 700,
  marginBottom: "8px",
});

const EmptySectionText = () => (
  <Typography variant="body2" color="text.secondary" sx={{ py: 1 }}>
    Sin información
  </Typography>
);

export default function SolicitudV4Detail() {
  const { uuid } = useParams();
  const navigate = useNavigate();
  const [payload, setPayload] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const loadDetail = useCallback(async () => {
    if (!uuid) {
      setLoading(false);
      setError("Sin identificador de solicitud.");
      setPayload(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWhatsappSchemaSolicitudByUuid(uuid);
      setPayload(withBogotaStart(data));
    } catch (e) {
      setError(
        e?.response?.data?.message ||
          e?.message ||
          "No se pudo cargar la solicitud."
      );
    } finally {
      setLoading(false);
    }
  }, [uuid]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const showFullPageLoader = loading && !payload;

  const sortedSteps = useMemo(
    () => sortSteps(payload?.steps),
    [payload?.steps]
  );

  const activeStepIndex = useMemo(
    () => getActiveStepIndex(sortedSteps, payload?.stepUuid),
    [sortedSteps, payload?.stepUuid]
  );

  const commerceFields = useMemo(
    () => (payload ? buildSectionFieldValues(payload, COMMERCE_FIELDS) : []),
    [payload]
  );

  const clientEmpty = useMemo(() => {
    if (!payload) return true;
    const rootEmpty = isSectionEmpty(payload, CLIENT_ROOT_FIELDS);
    const nestedEmpty = isSectionEmpty(payload.client, CLIENT_NESTED_FIELDS);
    return rootEmpty && nestedEmpty;
  }, [payload]);

  const clientFields = useMemo(() => {
    if (!payload || clientEmpty) return [];
    return buildClientFieldValues(
      payload,
      CLIENT_ROOT_FIELDS,
      payload.client,
      CLIENT_NESTED_FIELDS
    );
  }, [payload, clientEmpty]);

  const viabilityEmpty = useMemo(
    () => !payload || isSectionEmpty(payload.viability, VIABILITY_FIELDS),
    [payload]
  );

  const viabilityFields = useMemo(
    () =>
      payload && !viabilityEmpty
        ? buildSectionFieldValues(payload.viability, VIABILITY_FIELDS)
        : [],
    [payload, viabilityEmpty]
  );

  const creditEmpty = useMemo(
    () => !payload || isSectionEmpty(payload.credit, CREDIT_FIELDS),
    [payload]
  );

  const creditFields = useMemo(
    () =>
      payload && !creditEmpty
        ? buildSectionFieldValues(payload.credit, CREDIT_FIELDS)
        : [],
    [payload, creditEmpty]
  );

  const identityPhotos = useMemo(
    () =>
      [
        { label: "Documento frontal", url: payload?.identityImages?.frontUrl },
        { label: "Documento dorsal", url: payload?.identityImages?.reverseUrl },
        { label: "Selfie", url: payload?.identityImages?.selfieUrl },
      ].filter((photo) => photo.url),
    [payload]
  );

  return (
    <React.Fragment>
      <CssBaseline />
      <br />
      <Box
        sx={{
          width: "100%",
          minWidth: 0,
          boxSizing: "border-box",
          marginTop: "1.7rem",
        }}
      >
        <Container>
          <Item />
          <Item>.</Item>

          <Box mt={4} mb={2.5}>
            <Stack
              direction="row"
              alignItems="center"
              flexWrap="wrap"
              spacing={1.5}
              useFlexGap
              sx={{ columnGap: 1.5, rowGap: 1 }}
            >
              <Typography
                component="h1"
                variant="h5"
                sx={{
                  m: 0,
                  color: "#161e55",
                  fontWeight: 700,
                  lineHeight: 1.3,
                  mr: { xs: 0, sm: 4 },
                }}
              >
                Detalle solicitud V4
              </Typography>
              <ButtonAdd
                variant="contained"
                disableElevation
                onClick={loadDetail}
                disabled={loading}
              >
                Actualizar
              </ButtonAdd>
              <ButtonSecondary
                variant="outlined"
                startIcon={<ChatIcon />}
                disabled={!payload?.documentNumberClient}
                onClick={() =>
                  navigate(
                    `/homeAdmin/solicitudes/v4/conversacion/cliente/${payload.documentNumberClient}`,
                    {
                      state: {
                        nameClient: payload?.nameClient,
                        commerceName: payload?.commerceName,
                        cellphoneClient: payload?.client?.cellphone,
                        cellphoneCommerce: payload?.cellphoneCommerce,
                      },
                    }
                  )
                }
              >
                Conversación cliente
              </ButtonSecondary>
              <ButtonSecondary
                variant="outlined"
                startIcon={<ChatIcon />}
                disabled={!payload?.cellphoneCommerce}
                onClick={() =>
                  navigate(
                    `/homeAdmin/solicitudes/v4/conversacion/comercio/${payload.cellphoneCommerce}`,
                    {
                      state: {
                        commerceName: payload?.commerceName,
                        cellphoneCommerce: payload?.cellphoneCommerce,
                        nameClient: payload?.nameClient,
                      },
                    }
                  )
                }
              >
                Conversación comercio
              </ButtonSecondary>
              <ButtonSecondary
                variant="outlined"
                startIcon={<ArrowBackIcon />}
                onClick={() => navigate("/homeAdmin/solicitudes")}
              >
                Volver
              </ButtonSecondary>
            </Stack>
          </Box>

          {showFullPageLoader ? (
            <Box display="flex" justifyContent="center" py={6}>
              <CircularProgress sx={{ color: "#161e55" }} />
            </Box>
          ) : null}

          {!showFullPageLoader && error ? <Alert severity="error">{error}</Alert> : null}

          {!showFullPageLoader && !error && payload ? (
            <Stack spacing={2} sx={{ overflow: "visible", py: 0.5 }}>
              <Paper
                elevation={0}
                sx={{
                  p: 2,
                  borderRadius: "18px",
                  bgcolor: "rgba(22, 30, 85, 0.06)",
                  border: "1px solid rgba(22, 30, 85, 0.12)",
                }}
              >
                <Stack
                  direction="row"
                  flexWrap="wrap"
                  alignItems="center"
                  gap={1.5}
                  useFlexGap
                >
                  <Typography variant="body2" color="text.secondary">
                    <strong>Documento:</strong>{" "}
                    {formatFieldValue(payload.documentNumberClient, {
                      key: "documentNumberClient",
                      label: "",
                    })}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    <strong>Cliente:</strong>{" "}
                    {formatFieldValue(payload.nameClient, {
                      key: "nameClient",
                      label: "",
                    })}
                  </Typography>
                  {payload.stepName ? (
                    <Chip
                      label={payload.stepName}
                      size="small"
                      sx={{
                        bgcolor: "#161e55",
                        color: "#fff",
                        fontWeight: 600,
                      }}
                    />
                  ) : null}
                  {formatConversationStatus(payload.conversationStatus) ? (
                    <Chip
                      label={formatConversationStatus(payload.conversationStatus)}
                      size="small"
                      color={payload.conversationStatus === "REJECTED" ? "error" : "default"}
                      sx={{ fontWeight: 600 }}
                    />
                  ) : null}
                  <Typography variant="body2" color="text.secondary">
                    <strong>Inicio:</strong> {formatDate(payload.startDate)}{" "}
                    {payload.startTime ? `· ${payload.startTime}` : ""}
                  </Typography>
                </Stack>
              </Paper>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Comercio</SectionTitle>
                  <DetailFieldGrid fields={commerceFields} />
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Cliente</SectionTitle>
                  {clientEmpty ? (
                    <EmptySectionText />
                  ) : (
                    <DetailFieldGrid fields={clientFields} />
                  )}
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Pasos del flujo</SectionTitle>
                  {sortedSteps.length === 0 ? (
                    <EmptySectionText />
                  ) : (
                    <Stack spacing={2}>
                      {payload.stepName ? (
                        <Typography variant="body2" color="text.secondary">
                          Paso actual: <strong>{payload.stepName}</strong>
                        </Typography>
                      ) : null}
                      <Box
                        sx={{
                          width: "100%",
                          maxWidth: "100%",
                          overflowX: "auto",
                          overflowY: "visible",
                          pb: 1,
                          boxSizing: "border-box",
                        }}
                      >
                        <Stepper
                          activeStep={activeStepIndex >= 0 ? activeStepIndex : -1}
                          alternativeLabel
                          sx={{
                            width: "max-content",
                            minWidth: "100%",
                            "& .MuiStepLabel-label": {
                              fontSize: "0.75rem",
                            },
                          }}
                        >
                          {sortedSteps.map((step) => (
                            <Step key={step.stepUuid || step.labelSnapshot}>
                              <StepLabel>{step.labelSnapshot}</StepLabel>
                            </Step>
                          ))}
                        </Stepper>
                      </Box>
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Viabilidad</SectionTitle>
                  {viabilityEmpty ? (
                    <EmptySectionText />
                  ) : (
                    <DetailFieldGrid fields={viabilityFields} />
                  )}
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Crédito</SectionTitle>
                  {creditEmpty ? (
                    <EmptySectionText />
                  ) : (
                    <DetailFieldGrid fields={creditFields} />
                  )}
                </CardContent>
              </SectionCard>

              <SectionCard>
                <CardContent>
                  <SectionTitle variant="h6">Fotos</SectionTitle>
                  {identityPhotos.length === 0 ? (
                    <EmptySectionText />
                  ) : (
                    <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
                      {identityPhotos.map((photo) => (
                        <Box key={photo.label} sx={{ flex: 1, minWidth: 0 }}>
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                            {photo.label}
                          </Typography>
                          <a href={photo.url} target="_blank" rel="noopener noreferrer">
                            <Box
                              component="img"
                              src={photo.url}
                              alt={photo.label}
                              sx={{ width: "100%", maxWidth: 360, borderRadius: 1 }}
                            />
                          </a>
                        </Box>
                      ))}
                    </Stack>
                  )}
                </CardContent>
              </SectionCard>
            </Stack>
          ) : null}
        </Container>
      </Box>
    </React.Fragment>
  );
}
